import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useInterval } from 'react-use';

import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { commonCardMarketplaceFragment } from '@sorare/core/src/contexts/currentUser/queries';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { payment as paymentGlossary } from '@sorare/core/src/lib/glossary';

import type {
  PollInGameCurrencyPackDelivery,
  PollInGameCurrencyPackDeliveryVariables,
} from './__generated__/usePollInGameCurrencyPackDelivery.graphql.ts';

const POLL_IN_GAME_CURRENCY_PACK_DELIVERY_QUERY = gql`
  query PollInGameCurrencyPackDelivery($sport: Sport!) {
    currentUser {
      slug
      gemsBalances: inGameCurrencyBalances(
        sport: $sport
        inGameCurrencies: [COMMON_GEM]
      ) {
        amount
      }
      ...commonCardMarketplace_currentUser
    }
  }
  ${commonCardMarketplaceFragment}
` as TypedDocumentNode<
  PollInGameCurrencyPackDelivery,
  PollInGameCurrencyPackDeliveryVariables
>;

export const usePollInGameCurrencyPackDelivery = (
  cb: (success: boolean) => void
): { polling: boolean; startPolling: () => void } => {
  const { formatMessage } = useIntl();
  const [polling, setPolling] = useState(false);
  const [timeoutPolling, setTimeoutPolling] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const { sport } = useSportContext();
  const { currentUser } = useCurrentUserContext();
  const currentBalance = currentUser?.gemsBalances[0]?.amount || 0;
  const [startingBalance] = useState(currentBalance);
  const { showNotification } = useSnackNotificationContext();

  useQuery(POLL_IN_GAME_CURRENCY_PACK_DELIVERY_QUERY, {
    variables: { sport },
    pollInterval: polling ? 1000 : 0,
    skip: !polling,
  });

  const callbackWrapper = useCallback(
    (success: boolean) => {
      if (timeoutPolling) {
        clearTimeout(timeoutPolling);
      }
      setPolling(false);
      cb(success);
    },
    [cb, timeoutPolling]
  );

  const startPolling = useCallback(() => {
    setPolling(true);
    setTimeoutPolling(
      setTimeout(() => {
        showNotification('errors', {
          errors: [formatMessage(paymentGlossary.confirmationTimeout)],
        });
        callbackWrapper(false);
      }, 20000)
    );
  }, [callbackWrapper, formatMessage, showNotification]);

  useInterval(
    () => {
      if (startingBalance !== currentBalance) {
        setPolling(false);
        callbackWrapper(true);
      }
    },
    polling ? 200 : null
  );

  return { polling, startPolling };
};
